import * as Yup from 'yup';
import { useWatch, useFormContext } from 'react-hook-form';
import {
  Alert,
  AlertTitle,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material';
import { PackagingOptionsMap } from 'types/inventory';
import { RfqLiteSchema, RfqLineSchema } from 'types/sourcing';
import { useGetRfqLinesQuery } from 'store/slices/clientV2/sourcing';
import FormSection from 'ui-component/FormSection';
import {
  HookFormDateDurationInput,
  HookFormInput,
  HookFormToggleButton,
} from 'ui-component/HookFormComponents';
import { HookFormNumberInput } from 'ui-component/HookFormComponents/HookFormNumberInput';
import { HookFormTableInput } from 'ui-component/HookFormComponents/HookFormTableInput';
import { HookFormToggle } from 'ui-component/HookFormComponents/HookFormToggle';
import { HookFormSelect } from 'ui-component/HookFormComponents/HookFormSelect';
import { HookQuerySearchSelect } from 'ui-component/HookFormComponents/HookQuerySearchSelect';
import {
  IconInfinity,
  IconSparkles,
  IconSquareCheck,
  IconSquareX,
} from '@tabler/icons-react';
import AccordionCard from 'ui-component/cards/AccordionCard';
import { useEffect, useState } from 'react';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import PartV2Picker from 'ui-component/PartV2Picker';
import _ from 'lodash';
import { ALL, Lite, QueryFilterOperators } from 'types/api';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import {
  UNLIMITED_QUANTITY,
  UNLIMITED_QUANTITY_STRING,
} from 'constants/procurementConstants';
import { RequestPersistenceType, useAsyncRequest } from 'hooks/useAsyncRequest';
import {
  useLazyGetOrgPartsQuery,
  useScrapeOrgPartMutation,
} from 'store/slices/clientV2/parts';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import Chip from 'ui-component/extended/Chip';
import { handleErr } from 'utils/functions';
import {
  OrgPartSchema,
  OrgPartSchemaLite,
  OrgPartScrapeResponseSchema,
} from 'types/clientV2/parts';
import useSnackbar from 'hooks/useSnackbar';
import { useTheme } from '@mui/material/styles';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';
import { HookFormUnitOfMeasureAutocomplete } from 'ui-component/HookFormComponents/HookFormUnitOfMeasureAutocomplete';
import {
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { skipToken } from '@reduxjs/toolkit/query';

type QuoteLineFormProps = {
  rfq?: RfqLiteSchema | null;
  partPreSelected?: boolean;
  rfqLineId?: string | null;
  orgId?: string | null;
};

export const quoteLineFormConstants = {
  supplierQuote: { id: 'supplierQuote' as const, label: 'Quote' },
  part: { id: 'part' as const, label: 'Part' },
  v2OrgPart: { id: 'v2OrgPart' as const, label: 'Part' },
  description: { id: 'description' as const, label: 'Description' },
  isAuthorized: { id: 'isAuthorized', label: 'Authorized Distributor' },
  dimensionValue: { id: 'dimensionValue', label: 'Unit Quantity' },
  dimensionUnit: { id: 'dimensionUnit', label: 'Unit' },
  dimensionClass: { id: 'dimensionClass', label: 'Dimension Class' },
  moq: { id: 'moq', label: 'Minimum Order Quantity' },
  multiple: { id: 'multiple', label: 'Multiple' },
  maximum: { id: 'maximum', label: 'Maximum Available From Supplier' },
  prices: { id: 'prices', label: 'Prices' },
  to: { id: 'to', label: 'Price Break' },
  from: { id: 'from', label: 'Price Break' },
  unitPrice: { id: 'unitPrice', label: 'Unit Price' },
  shipsInLead: { id: 'shipsInLead', label: 'Ships in Lead Time' },
  noBid: { id: 'noBid' as const, label: 'Response' },
  singleUse: { id: 'singleUse', label: 'Can Only Be Purchased Once' },
  notes: { id: 'notes', label: 'Notes' },
  packaging: { id: 'packaging', label: 'Packaging' },
  rfqLine: { id: 'rfqLine', label: 'RFQ Line' },
  scrapedFields: { id: 'scrapedFields', label: 'Scraped Fields' },
  link: {
    id: 'link',
    label: 'Link',
  },
  stockDocument: { id: 'stockDocumentId', label: 'Stock Document' },
  sku: { id: 'sku', label: 'SKU' },
  coa: { id: 'coa', label: 'COA' },
};

export const createQuoteLineValidationSchema = (tempUoM: boolean) =>
  Yup.object().shape(
    {
      [quoteLineFormConstants.supplierQuote.id]: Yup.string()
        .required('Quote is required')
        .typeError('Quote is required'),
      [quoteLineFormConstants.v2OrgPart.id]: Yup.string().when(
        [quoteLineFormConstants.description.id],
        {
          is: (description: string) => !description,
          then: Yup.string().required('Part is required'),
        }
      ),
      [quoteLineFormConstants.description.id]: Yup.string().when(
        [quoteLineFormConstants.v2OrgPart.id],
        {
          is: (part: string) => !part,
          then: Yup.string().required('Description is required'),
        }
      ),
      [quoteLineFormConstants.isAuthorized.id]: Yup.boolean().nullable(),
      [quoteLineFormConstants.dimensionValue.id]: tempUoM
        ? Yup.number().nullable().min(0).required('Quantity is required')
        : Yup.number().nullable().min(0),
      [quoteLineFormConstants.dimensionUnit.id]: tempUoM
        ? Yup.string().required('Unit is required')
        : Yup.string().nullable(),
      [quoteLineFormConstants.dimensionClass.id]: Yup.string(),
      [quoteLineFormConstants.moq.id]: Yup.number().nullable().min(0),
      [quoteLineFormConstants.multiple.id]: Yup.number().nullable().min(1),
      [quoteLineFormConstants.maximum.id]: Yup.mixed()
        .test(
          'is-number-or-unlimited',
          'Maximum must be a number or "Unlimited"',
          (value) =>
            value === null ||
            value === undefined ||
            typeof value === 'number' ||
            !!_.toNumber(value) ||
            value === UNLIMITED_QUANTITY_STRING
        )
        .when([quoteLineFormConstants.noBid.id], {
          is: (noBid: boolean) => !noBid,
          then: Yup.mixed().required('Maximum is required'),
          otherwise: Yup.mixed().nullable(true),
        }),
      [quoteLineFormConstants.prices.id]: Yup.array()
        .of(
          Yup.object().shape({
            to: Yup.number()
              .min(0)
              .nullable()
              .transform((value, originalValue) =>
                typeof originalValue === 'string' && originalValue.trim() === ''
                  ? null
                  : value
              ),
            unitPrice: Yup.number()
              .min(0)
              .nullable()
              .transform((value, originalValue) =>
                typeof originalValue === 'string' && originalValue.trim() === ''
                  ? null
                  : value
              ),
          })
        )
        .compact()
        .nullable(),
      [quoteLineFormConstants.shipsInLead.id]: Yup.number().min(0).nullable(),
      [quoteLineFormConstants.noBid.id]: Yup.boolean().nullable(),
      [quoteLineFormConstants.singleUse.id]: Yup.boolean().nullable(),
      [quoteLineFormConstants.notes.id]: Yup.string().nullable(),
      [quoteLineFormConstants.packaging.id]: Yup.string().nullable(),
      [quoteLineFormConstants.rfqLine.id]: Yup.string().nullable(),
      [quoteLineFormConstants.scrapedFields.id]: Yup.array()
        .of(Yup.string())
        .nullable(),
      [quoteLineFormConstants.link.id]: Yup.string()
        .nullable()
        .trim()
        .max(2048, 'Link must be 2048 characters or fewer')
        .url('Please enter a valid URL'),
      [quoteLineFormConstants.coa.id]: Yup.string().nullable(),
      [quoteLineFormConstants.sku.id]: Yup.string().nullable(),
      [quoteLineFormConstants.stockDocument.id]: Yup.string().nullable(),
    },
    [
      [
        quoteLineFormConstants.v2OrgPart.id,
        quoteLineFormConstants.description.id,
      ],
      [quoteLineFormConstants.maximum.id, quoteLineFormConstants.noBid.id],
    ]
  );

type RfqLineSubschema = Lite<RfqLineSchema> & {
  v2OrgPart: Pick<
    OrgPartSchemaLite,
    'mpn' | 'manufacturer' | 'cofactrId' | 'customId'
  > | null;
};

export default function QuoteLineForm({
  rfq,
  partPreSelected = false,
  rfqLineId,
  orgId,
}: QuoteLineFormProps) {
  const tempScrapePart = useFeature(FeatureFlags.tempScrapePart).on;
  const tempUoM = useFeature(FeatureFlags.tempUoM).on;
  const theme = useTheme();
  const { dispatchErrorSnackbar } = useSnackbar();

  const [scrapedUrl, setScrapedUrl] = useState<string | null>(null);
  const [scrapeOrgPart, { isFetching: isLoadingScrapeOrgPart }] =
    useAsyncRequest(useScrapeOrgPartMutation, RequestPersistenceType.component);

  const [getOrgPart, { isFetching: isFetchingGetOrgPart }] =
    useServerSideLazyQuery<OrgPartSchema, Lite<OrgPartSchema>>(
      useLazyGetOrgPartsQuery
    );

  const { data: { data: rfqLines = [] } = { data: [] } } = useServerSideQuery<
    RfqLineSchema,
    RfqLineSubschema
  >(
    useGetRfqLinesQuery,
    rfq && !rfqLineId
      ? {
          schema: [
            ALL,
            { v2OrgPart: ['mpn', 'manufacturer', 'cofactrId', 'customId'] },
          ],
          filters: [
            {
              field: 'rfq',
              operator: QueryFilterOperators['='],
              value: rfq.id,
            },
          ],
        }
      : skipToken
  );

  const {
    control,
    formState: { errors, dirtyFields },
    setValue,
    register,
  } = useFormContext();

  const watchNoBid = useWatch({
    control,
    name: quoteLineFormConstants.noBid.id,
  });

  const watchPart = useWatch({
    control,
    name: quoteLineFormConstants.v2OrgPart.id,
  });

  const watchDescription = useWatch({
    control,
    name: quoteLineFormConstants.description.id,
  });

  const watchScrapedFields = useWatch({
    control,
    name: quoteLineFormConstants.scrapedFields.id,
  });

  const watchLink = useWatch({
    control,
    name: quoteLineFormConstants.link.id,
  });

  const watchDimensionClass = useWatch({
    control,
    name: quoteLineFormConstants.dimensionClass.id,
  });

  const watchDimensionUnit = useWatch({
    control,
    name: quoteLineFormConstants.dimensionUnit.id,
  });

  const watchDimensionValue = useWatch({
    control,
    name: quoteLineFormConstants.dimensionValue.id,
  });

  const watchRfqLine = useWatch({
    control,
    name: quoteLineFormConstants.rfqLine.id,
  });

  useEffect(() => {
    if (watchDimensionUnit) {
      const newDimensionClass =
        UnitOfMeasureDetails[watchDimensionUnit as UnitOfMeasureOptions]
          .dimensionClass;
      setValue(quoteLineFormConstants.dimensionClass.id, newDimensionClass, {
        shouldValidate: true,
      });
    }
  }, [watchDimensionUnit, setValue]);

  const handleMatchRfqLine = async () => {
    try {
      if (!watchRfqLine && !!watchPart) {
        const partResults = await getOrgPart({
          pageSize: 1,
          filters: [
            {
              field: 'id',
              operator: QueryFilterOperators['='],
              value: watchPart,
            },
          ],
          schema: [ALL],
        }).unwrap();
        const foundPart = partResults.data?.[0];
        if (foundPart) {
          const matchingRfqLines = _.filter(
            rfqLines,
            (rfqLine) =>
              rfqLine.v2OrgPart?.mpn === foundPart.mpn ||
              rfqLine.v2OrgPart?.customId === foundPart.customId
          );
          if (matchingRfqLines.length > 0) {
            setValue(quoteLineFormConstants.rfqLine.id, matchingRfqLines[0].id);
          }
        }
      } else if (!watchRfqLine && !!watchDescription) {
        const matchingRfqLines = _.filter(
          rfqLines,
          (rfqLine) => rfqLine.description === watchDescription
        );
        if (matchingRfqLines.length > 0) {
          setValue(quoteLineFormConstants.rfqLine.id, matchingRfqLines[0].id);
        }
      }
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        dispatchErrorSnackbar(errMessage);
      });
    }
  };

  const handleScrapePart = async () => {
    if (watchLink) {
      try {
        const response = (await scrapeOrgPart({
          url: watchLink,
          includePartData: false,
          includeSupplierQuoteData: true,
        })) as OrgPartScrapeResponseSchema;

        setScrapedUrl(watchLink);

        const mpn = response.partData.mpn;
        let foundPart = null;
        if (mpn && !partPreSelected) {
          const partResults = await getOrgPart({
            pageSize: 1,
            search: mpn,
            schema: [ALL],
          }).unwrap();
          foundPart = partResults.data?.[0];
          if (foundPart) {
            setValue(quoteLineFormConstants.v2OrgPart.id, foundPart.id);
          }
        }

        setValue(quoteLineFormConstants.scrapedFields.id, [
          ..._.chain(response.supplierQuoteData)
            .pickBy((value) => !!value)
            .keys()
            .map((k) =>
              _.get(
                {
                  shipsInLead: quoteLineFormConstants.shipsInLead.id,
                  sku: quoteLineFormConstants.sku.id,
                  packaging: quoteLineFormConstants.packaging.id,
                  coa: quoteLineFormConstants.coa.id,
                  moq: quoteLineFormConstants.moq.id,
                  multiple: quoteLineFormConstants.multiple.id,
                  maximum: quoteLineFormConstants.maximum.id,
                  prices: quoteLineFormConstants.prices.id,
                  dimensionUnit: quoteLineFormConstants.dimensionUnit.id,
                  dimensionClass: quoteLineFormConstants.dimensionClass.id,
                  dimensionValue: quoteLineFormConstants.dimensionValue.id,
                },
                k,
                null
              )
            )
            .compact()
            .value(),
        ]);
        setValue(
          quoteLineFormConstants.stockDocument.id,
          response.stockDocument
        );
        setValue(
          quoteLineFormConstants.shipsInLead.id,
          response.supplierQuoteData.shipsInLead
        );
        setValue(quoteLineFormConstants.sku.id, response.supplierQuoteData.sku);
        setValue(
          quoteLineFormConstants.packaging.id,
          response.supplierQuoteData.packaging
        );
        setValue(quoteLineFormConstants.coa.id, response.supplierQuoteData.coa);
        setValue(
          quoteLineFormConstants.moq.id,
          _.toNumber(response.supplierQuoteData.moq) || 1
        );
        setValue(
          quoteLineFormConstants.multiple.id,
          _.toNumber(response.supplierQuoteData.multiple) ||
            _.toNumber(response.supplierQuoteData.packageQuantity) ||
            1
        );
        setValue(
          quoteLineFormConstants.maximum.id,
          _.toNumber(response.supplierQuoteData.maximum) === UNLIMITED_QUANTITY
            ? UNLIMITED_QUANTITY_STRING
            : _.toNumber(response.supplierQuoteData.maximum) ||
                UNLIMITED_QUANTITY_STRING
        );
        setValue(
          quoteLineFormConstants.dimensionUnit.id,
          response.supplierQuoteData.dimensionUnit ||
            response.partData.preferredUnit ||
            foundPart?.preferredUnit ||
            UnitOfMeasureOptions.PIECE
        );
        setValue(
          quoteLineFormConstants.dimensionClass.id,
          UnitOfMeasureDetails[
            quoteLineFormConstants.dimensionUnit.id as UnitOfMeasureOptions
          ].dimensionClass
        );
        setValue(
          quoteLineFormConstants.dimensionValue.id,
          response.supplierQuoteData.packageQuantity || 1
        );
        setValue(
          quoteLineFormConstants.prices.id,
          _.map(response.supplierQuoteData.prices || [], (price) => ({
            to: price.from,
            unitPrice: price.unitPrice,
          }))
        );
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    }
  };

  return (
    <>
      {tempScrapePart && (
        <FormSection>
          <HookFormInput
            control={control}
            errors={errors}
            name={quoteLineFormConstants.link.id}
            label={quoteLineFormConstants.link.label}
          />
          {(watchLink !== scrapedUrl || !scrapedUrl || !watchLink) &&
            tempScrapePart && (
              <Alert
                severity="success"
                variant="standard"
                icon={false}
                action={
                  <SegmentLoadingButton
                    onClick={handleScrapePart}
                    variant="contained"
                    loading={isLoadingScrapeOrgPart || isFetchingGetOrgPart}
                    disabled={!watchLink}
                  >
                    AI Analyze
                  </SegmentLoadingButton>
                }
              >
                <AlertTitle>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <IconSparkles />
                    <Typography variant="h5">
                      Add to Quote with Ailana
                    </Typography>
                    <Chip label="BETA" size="small" color="primary" />
                  </Stack>
                </AlertTitle>
                {watchLink
                  ? 'Click AI Analyze to automatically generate a quote line with Ailana. Please be patient as this may take several seconds.'
                  : 'Enter a Link to automatically generate a quote line with Ailana'}
              </Alert>
            )}
        </FormSection>
      )}
      {!partPreSelected && (
        <FormSection>
          <PartV2Picker
            onChange={(parts) => {
              const newValue = parts?.[0]?.id ?? '';
              setValue(quoteLineFormConstants.v2OrgPart.id, newValue, {
                shouldValidate: true,
              });
              if (!dirtyFields[quoteLineFormConstants.dimensionUnit.id]) {
                // Only update the dimension fields if the user hasn't manually set them (ie, don't override '400 ft' to '400 in')
                setValue(
                  quoteLineFormConstants.dimensionClass.id,
                  parts?.[0]?.dimensionClass ?? '',
                  {
                    shouldValidate: true,
                  }
                );
                setValue(
                  quoteLineFormConstants.dimensionUnit.id,
                  parts?.[0]?.preferredUnit ?? '',
                  {
                    shouldValidate: true,
                  }
                );
              }
              handleMatchRfqLine();
            }}
            initiallySelectedParts={watchPart ? [watchPart] : undefined}
            errors={errors}
            name={quoteLineFormConstants.v2OrgPart.id}
            disabled={Boolean(watchDescription)}
            overrideOrgId={orgId || undefined}
          />
          <Divider sx={{ my: 2 }}>OR</Divider>
          <HookFormInput
            name={quoteLineFormConstants.description.id}
            label={quoteLineFormConstants.description.label}
            control={control}
            errors={errors}
            disabled={Boolean(watchPart)}
            onChange={handleMatchRfqLine}
          />
        </FormSection>
      )}
      <HookFormToggleButton
        name={quoteLineFormConstants.noBid.id}
        label={quoteLineFormConstants.noBid.label}
        control={control}
        errors={errors}
      >
        <ToggleButton value color="error">
          <IconSquareX style={{ marginRight: '10px' }} />
          No Bid
        </ToggleButton>
        <ToggleButton value={false} color="success">
          <IconSquareCheck style={{ marginRight: '10px' }} />
          Bid
        </ToggleButton>
      </HookFormToggleButton>
      {!watchNoBid && (
        <FormSection
          title={
            <Stack direction="row" alignItems="start" spacing={1}>
              {_.includes(
                watchScrapedFields,
                quoteLineFormConstants.prices.id
              ) && (
                <IconSparkles size={20} color={theme.palette.success.main} />
              )}
              <Typography variant="h4">Quantity, Price, and Lead</Typography>
            </Stack>
          }
        >
          {tempUoM && (
            <>
              <Divider sx={{ my: 2 }}>SOLD IN UNITS OF</Divider>
              <Stack direction="row" spacing={2}>
                <HookFormNumberInput
                  name={quoteLineFormConstants.dimensionValue.id}
                  label={quoteLineFormConstants.dimensionValue.label}
                  control={control}
                  errors={errors}
                  fullWidth
                  sx={{ flex: 1 }}
                  boxSx={{ flex: 1 }}
                />
                <HookFormUnitOfMeasureAutocomplete
                  name={quoteLineFormConstants.dimensionUnit.id}
                  label={quoteLineFormConstants.dimensionUnit.label}
                  control={control}
                  errors={errors}
                  limitToDimensionClass={watchDimensionClass}
                />
                <input
                  type="hidden"
                  {...register(quoteLineFormConstants.dimensionClass.id)}
                />
              </Stack>
            </>
          )}
          <>
            <Divider sx={{ my: 2 }}>AT A PRICE OF</Divider>
            <HookFormTableInput
              name={quoteLineFormConstants.prices.id}
              control={control}
              errors={errors}
              fields={[
                {
                  component: HookFormInput,
                  name: quoteLineFormConstants.to.id,
                  label: quoteLineFormConstants.to.label,
                  props: {
                    InputProps: {
                      endAdornment: tempUoM
                        ? `${watchDimensionValue} ${
                            _.get(
                              UnitOfMeasureDetails,
                              watchDimensionUnit,
                              UnitOfMeasureOptions.PIECE
                            )?.abbreviation
                          }`
                        : 'pc',
                    },
                  },
                },
                {
                  component: HookFormInput,
                  name: quoteLineFormConstants.unitPrice.id,
                  label: quoteLineFormConstants.unitPrice.label,
                  props: {
                    InputProps: {
                      startAdornment: '$',
                    },
                  },
                },
              ]}
            />
            <Divider sx={{ my: 2 }}>WITH A MINIMUM ORDER QUANTITY OF</Divider>
            <HookFormNumberInput
              name={quoteLineFormConstants.moq.id}
              label={quoteLineFormConstants.moq.label}
              control={control}
              errors={errors}
            />
            <Divider sx={{ my: 2 }}>AND AN ORDER MULTIPLE OF</Divider>
            <HookFormNumberInput
              name={quoteLineFormConstants.multiple.id}
              label={quoteLineFormConstants.multiple.label}
              control={control}
              errors={errors}
            />
            <Divider sx={{ my: 2 }}>BUT AT MOST</Divider>
            <HookFormInput
              name={quoteLineFormConstants.maximum.id}
              label={quoteLineFormConstants.maximum.label}
              control={control}
              errors={errors}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SegmentIconButton
                      onClick={() =>
                        setValue(
                          quoteLineFormConstants.maximum.id,
                          UNLIMITED_QUANTITY_STRING
                        )
                      }
                    >
                      <IconInfinity />
                    </SegmentIconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Divider sx={{ my: 2 }}>SHIPS IN</Divider>
            <HookFormDateDurationInput
              name={quoteLineFormConstants.shipsInLead.id}
              label={quoteLineFormConstants.shipsInLead.label}
              durationLabel={quoteLineFormConstants.shipsInLead.label}
              businessDays
              control={control}
              setValue={setValue}
            />
          </>
        </FormSection>
      )}
      {!watchNoBid && (
        <AccordionCard title="Other Settings" defaultExpanded={false}>
          <HookFormToggle
            name={quoteLineFormConstants.singleUse.id}
            label={quoteLineFormConstants.singleUse.label}
            control={control}
            errors={errors}
          />
          <HookFormToggle
            name={quoteLineFormConstants.isAuthorized.id}
            label={quoteLineFormConstants.isAuthorized.label}
            control={control}
            errors={errors}
          />
          <HookFormSelect
            name={quoteLineFormConstants.packaging.id}
            label={quoteLineFormConstants.packaging.label}
            control={control}
            errors={errors}
          >
            {Object.entries(PackagingOptionsMap).map(([id, label]) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </HookFormSelect>
        </AccordionCard>
      )}
      <HookFormInput
        name={quoteLineFormConstants.notes.id}
        label={quoteLineFormConstants.notes.label}
        control={control}
        errors={errors}
      />
      {!!rfq?.id && !rfqLineId && (
        <HookQuerySearchSelect<RfqLineSchema>
          name={quoteLineFormConstants.rfqLine.id}
          label={quoteLineFormConstants.rfqLine.label}
          control={control}
          errors={errors}
          useGetQuery={useGetRfqLinesQuery}
          getOptionLabel={(rfqOption: RfqLineSchema) => {
            const rawQuant = tempUoM
              ? rfqOption?.dimensionValue
              : rfqOption?.quant;
            const formattedQuant = rawQuant
              ? _.toNumber(rawQuant).toLocaleString('en-US')
              : '-';
            const unitAbbreviation = tempUoM
              ? UnitOfMeasureDetails[rfqOption.dimensionUnit]?.abbreviation
              : 'x';
            if (rfqOption.description) {
              return `${formattedQuant}${unitAbbreviation} ${rfqOption.description}`;
            }
            const { manufacturer, mpn, customId, cofactrId } =
              rfqOption?.v2OrgPart ?? {};
            return `${formattedQuant}${unitAbbreviation} ${
              manufacturer ?? ''
            } ${mpn ?? ''} ${customId ?? ''} ${
              cofactrId ? `(${cofactrId})` : ''
            }`;
          }}
          schema={[
            'id',
            'description',
            'quant',
            'dimensionValue',
            'dimensionUnit',
            'part',
            'org',
            { orgPart: ['mfg', 'mpn', 'customId'] },
            { v2OrgPart: ['mpn', 'manufacturer', 'cofactrId', 'customId'] },
          ]}
          filters={
            rfq?.id
              ? [
                  {
                    field: 'rfq',
                    operator: QueryFilterOperators['='],
                    value: rfq.id,
                  },
                ]
              : []
          }
        />
      )}
    </>
  );
}
